import { useReducer } from 'react';
import { LinksData } from '@/types';
import { ApiService } from '@/services/api';

interface DataState {
  data: LinksData | null;
  isLoading: boolean;
  error: string | null;
  saving: boolean;
}

type DataAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_DATA'; payload: LinksData | null }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOAD_SUCCESS'; payload: LinksData }
  | { type: 'UPDATE_SUCCESS'; payload: LinksData }
  | { type: 'CLEAR_ERROR' };

const initialState: DataState = {
  data: null,
  isLoading: false,
  error: null,
  saving: false,
};

function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload, error: null };
    case 'SET_SAVING':
      return { ...state, saving: action.payload, error: null };
    case 'SET_DATA':
      return { ...state, data: action.payload, isLoading: false, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false, saving: false };
    case 'LOAD_SUCCESS':
      return { ...state, data: action.payload, isLoading: false, error: null };
    case 'UPDATE_SUCCESS':
      return { ...state, data: action.payload, saving: false, error: null };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function useDataReducer() {
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const apiService = ApiService.getInstance();

  const loadData = async (): Promise<void> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      console.log('Loading data from API...');
      const response = await apiService.getCurrentData();
      
      console.log('Load data response:', response);
      
      if (response.success && response.data) {
        // Try different ways to extract the data
        let extractedData = null;
        
        if (response.data.data) {
          extractedData = response.data.data;
        } else if (response.data) {
          extractedData = response.data;
        }
        
        console.log('Extracted data for form:', extractedData);
        dispatch({ type: 'LOAD_SUCCESS', payload: extractedData });
      } else {
        console.error('API Load Error:', response.error);
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to load data' });
      }
    } catch (error) {
      console.error('Error loading data:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Network error occurred' });
    }
  };

  const updateData = async (newData: LinksData): Promise<boolean> => {
    dispatch({ type: 'SET_SAVING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });
    
    try {
      console.log('Updating data:', newData);
      const response = await apiService.updateData(newData);
      
      console.log('Update data response:', response);
      
      if (response.success && response.data) {
        // Try different ways to extract the updated data
        let extractedData = null;
        
        if (response.data.data) {
          extractedData = response.data.data;
        } else if (response.data) {
          extractedData = response.data;
        }
        
        console.log('Update successful, new data:', extractedData);
        dispatch({ type: 'UPDATE_SUCCESS', payload: extractedData });
        return true;
      } else {
        console.error('API Update Error:', response.error);
        dispatch({ type: 'SET_ERROR', payload: response.error || 'Failed to update data' });
        return false;
      }
    } catch (error) {
      console.error('Error updating data:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Network error occurred' });
      return false;
    }
  };

  const refreshData = async (): Promise<void> => {
    await loadData();
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return {
    ...state,
    loadData,
    updateData,
    refreshData,
    clearError,
  };
}